import { memo, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, VStack } from '@library';
import withErrorBoundary from '../../../../core/components/errors/withErrorBoundary';
import type { ProductItem, ProductWithRelatedData } from '../../../types';
import SpinnerPage from '../../../../core/components/pages/SpinnerPage';
import useProductsWithRelatedData from '../../../queries/useProductsWithRelatedData';
import ProductListMainContent from '../../ProductList/List/ProductListMainContent';
import { ProductsCarouselContent } from '../ProductsCarousel';
import useCarouselActions from '../../../../core/hooks/useCarouselActions';
import styles from './SpareParts.module.scss';

const useSpareParts = (spareParts: ProductItem[]) => {
    const { productsWithRelatedData, loading } =
        useProductsWithRelatedData(spareParts);

    const sortedSpareParts = useMemo(() => {
        return productsWithRelatedData
            ? (productsWithRelatedData.slice().sort((a, b) => {
                  const priceA = a.customerPrice?.retailPrice ?? 0;
                  const priceB = b.customerPrice?.retailPrice ?? 0;
                  return priceA - priceB;
              }) as ProductItem[])
            : [];
    }, [productsWithRelatedData]);

    return {
        loading,
        spareParts: sortedSpareParts as ProductWithRelatedData[],
    };
};

const SpareParts = ({
    spareParts: sparePartsProps,
}: {
    spareParts: ProductItem[];
}) => {
    const { loading, spareParts } = useSpareParts(sparePartsProps);

    const { nextItem, previousItem, controlsState, handleChangeActions } =
        useCarouselActions({ watchControlState: true });

    const [isExpanded, setIsExpanded] = useState(false);

    const toggleView = (value: boolean) => setIsExpanded(value);

    return (
        <div data-test-class="productTableCollapsibleTrigger">
            {loading ? (
                <SpinnerPage />
            ) : (
                <div className={styles.spareParts}>
                    <VStack gap="smallMedium">
                        {controlsState.showControls && (
                            <div className={styles.button}>
                                <Button
                                    outline
                                    onClick={() => toggleView(!isExpanded)}
                                >
                                    <FormattedMessage
                                        id={
                                            isExpanded
                                                ? 'Product ProductDetails SpareParts Reduce'
                                                : 'Product ProductDetails SpareParts Expand'
                                        }
                                    />
                                </Button>
                            </div>
                        )}
                        {isExpanded ? (
                            <ProductListMainContent
                                viewMode="grid"
                                products={spareParts}
                            />
                        ) : (
                            <ProductsCarouselContent
                                onChangeActions={handleChangeActions}
                                controlsState={controlsState}
                                previousItem={previousItem}
                                nextItem={nextItem}
                                products={spareParts}
                            />
                        )}
                    </VStack>
                </div>
            )}
        </div>
    );
};

export default memo(withErrorBoundary(SpareParts));
